import { useState, useEffect, useRef } from "react";
import { duplicateProps } from "../../../refractCommon/miro/duplicateProps";
import { createItem } from "../../../refractCommon/miro/create";
import { removeHtml } from "../../../refractCommon/removeHtml";
import { sortLeftRightTopBottom } from "../../../refractCommon/miro/sortLeftRightTopBottom";
import ToolWrapper from "../../PowerPack_Common/ToolWrapper";
import { runFilterOrderValidateSelectedItems } from "../../Utils/runFilterOrderValidateSelectedItems";

const miro = window.miro;

const toolConfig = {
  tool_name: "Merge",
  tool_icon: "icon-collapse",
  tool_short_description: "Merge text from many items into one.",
  tool_id: "merge-tool",
  works_on: "many to one",
  tool_type_icons: (
    <div className="cs5 ce12 grid">
      <div className="m2 icon icon-rectangle cs1"></div>
      <div className="m2 icon icon-sticky-bulk cs2"></div>
      <div className="m2 icon icon-text cs3"></div>
    </div>
  ),
  tool_is_in_uat: true,
  tool_is_pro: false,
};

export default function Merge({ boardSelection, userIsPro, environment }) {
  const [selectedValidItems, setSelectedValidItems] = useState([]);
  const [validSelectedSourceItems, setValidSelectedSourceItems] = useState([]);
  const [validSelectedTargetItem, setValidSelectedTargetItem] = useState([]);
  const [canSetSource, setCanSetSource] = useState(false);
  const [canSetTarget, setCanSetTarget] = useState(false);
  const [delimiter, setDelimiter] = useState(" ");
  const [warning, setWarning] = useState("");
  const [canMerge, setCanMerge] = useState(false);
  const [preview, setPreview] = useState("");
  const [boardId, setBoardId] = useState("");
  const [settingsButtonState, setSettingsButtonState] = useState({
    layout: "delimiter",
    links: false,
  });

  // SELECTION STUFF
  let itemValidator = {
    validItemTypes: ["sticky_note", "text", "shape"],
    source_min: 2,
    source_max: 20,
    target_min: 1,
    target_max: 1,
  };

  useEffect(() => {
    let validSelectItems = runFilterOrderValidateSelectedItems(
      boardSelection,
      itemValidator.validItemTypes
    );
    setSelectedValidItems(validSelectItems);
    canSetSourceOrTarget(validSelectItems);
  }, [boardSelection]);

  useEffect(async () => {
    let thisBoardInfo = await miro.board.getInfo();
    setBoardId(thisBoardInfo.id);
  }, []);

  useEffect(() => {
    createMergePreview();
    canWeMerge();
  }, [
    validSelectedSourceItems,
    validSelectedTargetItem,
    delimiter,
    settingsButtonState,
  ]);

  function canWeMerge() {
    if (
      validSelectedSourceItems.length >= 2 &&
      validSelectedTargetItem.length === 1
    ) {
      setCanMerge(true);
    } else {
      setCanMerge(false);
    }
  }

  function canSetSourceOrTarget(items) {
    // SOURCE
    if (
      items.length >= itemValidator.source_min &&
      items.length <= itemValidator.source_max
    ) {
      setCanSetSource(true);
    } else {
      setCanSetSource(false);
    }
    // TARGET
    if (
      items.length >= itemValidator.target_min &&
      items.length <= itemValidator.target_max
    ) {
      setCanSetTarget(true);
    } else {
      setCanSetTarget(false);
    }
  }

  function setSourceItems() {
    if (canSetSource) {
      setValidSelectedSourceItems(selectedValidItems);
    }
  }
  function setTargetItem() {
    if (canSetTarget) {
      setValidSelectedTargetItem(selectedValidItems);
    }
  }
  function handleDelimiter(event) {
    setDelimiter(event.target.value);
    // setPreview(merge(item?.content, event.target.value));
  }

  // FUNCTIONAL STUFF

  function createMergePreview() {
    setPreview(merge(validSelectedSourceItems, delimiter));
  }

  function merge(items, delimiter) {
    let htmlContent;
    if (items.length >= 2) {
      let delim = delimiter || " ";
      let finalStringArray = [];
      items.forEach((item) => finalStringArray.push(removeHtml(item.content)));
      let finalString = finalStringArray.join(delim);
      htmlContent = "<p class='p-small'>" + finalString + "</a></p>";
      return htmlContent;
    }
  }

  function handleMergeToBoard() {
    let items = validSelectedSourceItems;
    let targetItem = validSelectedTargetItem[0];
    if (items.length > 2) {
      let delim = delimiter || " ";
      let finalStringArray = [];
      items.forEach((item) => finalStringArray.push(removeHtml(item.content)));
      let finalString = finalStringArray.join(delim);
      let htmlContent = "<p class='p-small'>" + finalString + "</p>";
      targetItem.content = htmlContent;
      targetItem.sync();
    }
  }

  function handleSettingsButtonClicks(flag) {
    let newSettingsButtonState = {
      ...settingsButtonState,
    };
    switch (flag) {
      case "delimiter":
        if (newSettingsButtonState.layout !== "delimiter") {
          newSettingsButtonState.layout = "delimiter";
        } else {
          newSettingsButtonState.layout = "delimiter";
        }
        setSettingsButtonState(newSettingsButtonState);
        break;
      case "new-lines":
        if (newSettingsButtonState.layout !== "new-lines") {
          newSettingsButtonState.layout = "new-lines";
        } else {
          newSettingsButtonState.layout = "none";
        }
        setSettingsButtonState(newSettingsButtonState);
        break;
      case "bullets":
        if (newSettingsButtonState.layout !== "bullets") {
          newSettingsButtonState.layout = "bullets";
        } else {
          newSettingsButtonState.layout = "none";
        }
        setSettingsButtonState(newSettingsButtonState);
        break;
      case "numbered-bullets":
        if (newSettingsButtonState.layout !== "numbered-bullets") {
          newSettingsButtonState.layout = "numbered-bullets";
        } else {
          newSettingsButtonState.layout = "none";
        }
        setSettingsButtonState(newSettingsButtonState);
        break;
      case "board-links":
        newSettingsButtonState.links = !newSettingsButtonState.links;
        setSettingsButtonState(newSettingsButtonState);
        break;
      default:
        setSettingsButtonState(newSettingsButtonState);
    }
  }

  return (
    <ToolWrapper
      toolConfig={toolConfig}
      boardSelection={boardSelection}
      userIsPro={userIsPro}
      environment={environment}>
      <div className="cs1 ce12 p-medium mt-0">
        {toolConfig.tool_short_description}
        <div className="help-container grid cs1 ce12 mt-8 mb-0 pt-8">
          <p className="p-small cs1 ce4 mb-0 mt-0">
            Works on{" "}
            <b>
              {toolConfig.works_on.length < 5 ? (
                <span>{toolConfig.works_on}</span>
              ) : (
                <span>
                  <br />
                  {toolConfig.works_on}
                </span>
              )}
            </b>
          </p>
          {toolConfig.tool_type_icons}
        </div>
      </div>
      <div className="grid cs1 ce12 ">
        <div className="cs1 ce6 mb-0"></div>

        <div className="cs1 ce12 form-group mb-0 grid mt-0">
          <div className="cs1 ce5 grid">
            <div className="cs1 ce12 mb-0 mt-0 p-medium">Source Items: 2+</div>
            <button
              type="button"
              className={
                "button button-primary button-small cs1 ce10 mb-0 mt-0 button-unset ml-4" +
                (validSelectedSourceItems.length > 1
                  ? " button-set"
                  : " button-unset")
              }
              onClick={setSourceItems}
              disabled={!canSetSource}>
              Set
            </button>
            {/* <div className="h3 mt-0 mb-0 cs9 ce10 pl-0">
              {validSelectedSourceItems.length > 1 ? "👍" : "👎"}
            </div> */}
          </div>
          <div className="cs6 ce12 grid">
            <div className="cs1 ce12 mb-0 mt-0 p-medium">Target Item: 1</div>
            <button
              type="button"
              className={
                "button button-primary button-small cs1 ce8 mb-0 mt-0 button-unset " +
                (validSelectedTargetItem.length != 1
                  ? " button-unset"
                  : " button-set")
              }
              onClick={setTargetItem}
              disabled={!canSetTarget}>
              Set
            </button>
            {/* <div className="h3 mt-0 mb-0 cs9 ce10 pl-0">
              {validSelectedTargetItem.length != 1 ? "👎" : "👍"}
            </div> */}
            {/* <button
              type="button"
              className="button button-secondary button-small cs8 ce12"
              onClick={() => {
                setValidSelectedSourceItems([]);
                setValidSelectedTargetItem([]);
                setDelimiter("");
              }}>
              Clear
            </button> */}
            <button
              id="clear"
              value="clear"
              className="button button-secondary mr-0 ml-0 mb-0 button-small cs9 ce12 pl-12 mr-8"
              type="button"
              onClick={() => {
                setValidSelectedSourceItems([]);
                setValidSelectedTargetItem([]);
                setDelimiter("");
              }}>
              <span className="icon-trash no-click-event"></span>
            </button>
          </div>

          <div className="cs1 ce12 grid">
            <div className="cs1 ce12 mb-0 mt-8 p-medium">Layout:</div>
            <input
              id="delimiter"
              onClick={(e) => handleSettingsButtonClicks(e.target.id)}
              onChange={handleDelimiter}
              className={
                "input input-small mb-0 cs1 ce5 mt-0 mr-8 ml-4" +
                (settingsButtonState.layout === "delimiter"
                  ? " button-selected"
                  : "")
              }
              type="text"
              placeholder="Delimiter"
            />
            {/* <button
              id="new-lines"
              value="new-lines"
              onClick={(e) => handleSettingsButtonClicks(e.target.id)}
              className={
                "button button-secondary mr-0 ml-0 mb-0 button-small cs7 ce8 pl-8 mr-8" +
                (settingsButtonState.layout === "new-lines"
                  ? " button-selected"
                  : "")
              }
              type="button">
              <span className="icon-align-left no-click-event"></span>
            </button>
            <button
              onClick={(e) => handleSettingsButtonClicks(e.target.id)}
              id="bullets"
              value="bullets"
              className={
                "button button-secondary mr-0  ml-0 mb-0 button-small cs9 ce10 pl-8 mr-8" +
                (settingsButtonState.layout === "bullets"
                  ? " button-selected"
                  : "")
              }
              type="button">
              <span className="icon-bullet-list no-click-event"></span>
            </button>{" "}
            <button
              onClick={(e) => handleSettingsButtonClicks(e.target.id)}
              id="numbered-bullets"
              value="numbered-bullets"
              className={
                "button button-secondary mr-0 ml-0 mb-0 button-small cs11 ce12 pl-8 mr-8" +
                (settingsButtonState.layout === "numbered-bullets"
                  ? " button-selected"
                  : "")
              }
              type="button">
              <span className="icon-numbered-list no-click-event"></span>
            </button> */}
            {/* <button
              onClick={(e) => handleSettingsButtonClicks(e.target.id)}
              id="board-links"
              value="board-links"
              className={
                "button button-secondary mr-0 ml-0 mb-0 button-small cs11 ce12 pl-12 mr-4" +
                (settingsButtonState.links === true ? " button-selected" : "")
              }
              type="button">
              <span className="icon-link no-click-event"></span>
            </button> */}
          </div>
          {/* <div className="icon icon-link cs1"></div>
          <div className="cs2 ce10  p-medium mb-0">
            Miro links back to source items:
          </div>
          <div className="cs11 ce12 mt-16 ml--24">
            <label className="toggle">
              <input
                type="checkbox"
                tabIndex="0"
                onChange={() => handleSettingsButtonClicks("board-links")}
              />
              <span></span>
            </label>
          </div> */}

          <div className="cs1 ce12 mb-0   p-medium">Preview:</div>
          <div id="merge-preview" className="cs1 ce12">
            <div
              style={{
                color: "var(--indigo400)",
                display: preview === "" ? "block" : "none",
              }}
              className="p-small align-center">
              preview
            </div>
            <div dangerouslySetInnerHTML={{ __html: preview }}></div>
          </div>

          {/* <button
            type="button"
            className="button button-secondary button-small cs1 ce5 mb-0 mt-0"
            onClick={() => {
              setValidSelectedSourceItems([]);
              setValidSelectedTargetItem([]);
              setDelimiter("");
            }}>
            Clear All
          </button> */}
          <button
            type="button"
            disabled={!canMerge}
            className="button button-primary button-small cs1 ce12 mb-0 mt-0"
            onClick={handleMergeToBoard}>
            Merge
          </button>
        </div>
      </div>
    </ToolWrapper>
  );
}
